<script setup lang="ts">
  defineOptions({
    name: 'ConversationList',
  })

  import { computed, nextTick, onMounted, onUnmounted, ref, watch, type PropType } from 'vue'

  import { DEFAULT_IMAGES } from '@common/core-layer/constants'

  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import { useI18n } from 'vue-i18n'

  import { useSharedIntersectionObserver } from '@communities/posts-layer/composables/useSharedIntersectionObserver'

  import CPButton from '@ui/components/CPButton.vue'

  import CPText from '@ui/components/CPText.vue'

  import ConversationListItem from './ConversationListItem.vue'

  import ConversationCardShimmer from '../atoms/ConversationCardShimmer.vue'

  import type { IConversation } from '../../types/conversation.types'

  const props = defineProps({
    conversations: {
      type: Array as PropType<IConversation[]>,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isFetchingNextPage: {
      type: Boolean,
      default: false,
    },
    hasNextPage: {
      type: Boolean,
      default: false,
    },
    selectedConversationId: {
      type: String as PropType<string | null>,
      default: null,
    },
    currentUserId: {
      type: String,
      default: '',
    },
    showStartChatButton: {
      type: Boolean,
      default: true,
    },
  })

  const emit = defineEmits<{
    select: [conversation: IConversation]
    loadMore: []
    startChat: []
  }>()

  const { t } = useI18n()

  const listRef = ref<HTMLElement | null>(null)

  const loadMoreTriggerRef = ref<HTMLElement | null>(null)

  const isEmpty = computed(() => props.conversations.length === 0 && !props.isLoading)

  // Number of shimmer cards to show while loading
  const shimmerCount = ref(5)

  const shimmerRowHeight = ref(72)

  const resizeObserver = ref<ResizeObserver | null>(null)

  const sharedObserver = useSharedIntersectionObserver({
    root: null,
    rootMargin: '100px',
    threshold: 0.1,
  })

  // Setup intersection observer for infinite scroll
  const setupIntersectionObserver = () => {
    sharedObserver.disconnect()
    if (!loadMoreTriggerRef.value) return

    sharedObserver.observe(loadMoreTriggerRef.value, entry => {
      if (entry.isIntersecting && props.hasNextPage && !props.isFetchingNextPage && !props.isLoading) {
        emit('loadMore')
      }
    })
  }

  const updateShimmerCount = () => {
    if (!listRef.value) return

    const containerHeight = listRef.value.clientHeight || 0
    if (containerHeight === 0) return

    const shimmerCard = listRef.value.querySelector('#conversation-card-shimmer-1') as HTMLElement | null
    if (shimmerCard?.offsetHeight) {
      shimmerRowHeight.value = shimmerCard.offsetHeight + 1
    }

    const count = Math.ceil(containerHeight / shimmerRowHeight.value) + 1
    shimmerCount.value = Math.max(count, 5)
  }

  const setupResizeObserver = () => {
    if (!listRef.value || typeof ResizeObserver === 'undefined') return

    resizeObserver.value?.disconnect()
    resizeObserver.value = new ResizeObserver(() => {
      if (props.isLoading && props.conversations.length === 0) {
        updateShimmerCount()
      }
    })

    resizeObserver.value.observe(listRef.value)
  }

  const handleConversationClick = (conversation: IConversation) => {
    emit('select', conversation)
  }

  onMounted(() => {
    // Delay to ensure refs are available
    nextTick(() => {
      setupIntersectionObserver()
      setupResizeObserver()
      updateShimmerCount()
    })
  })

  onUnmounted(() => {
    sharedObserver.disconnect()
    resizeObserver.value?.disconnect()
  })

  // Watch for changes in conversations to re-register observer
  watch(
    () => [props.conversations.length, props.hasNextPage],
    () => {
      nextTick(() => {
        setupIntersectionObserver()
      })
    }
  )

  watch(
    () => [props.isLoading, props.conversations.length],
    () => {
      if (props.isLoading && props.conversations.length === 0) {
        nextTick(() => {
          updateShimmerCount()
        })
      }
    }
  )
</script>

<template>
  <div ref="listRef" class="custom-scrollbar flex-1 overflow-y-auto overscroll-contain" @wheel.stop @touchmove.stop>
    <!-- Loading state with shimmer cards -->
    <div v-if="isLoading && conversations.length === 0" class="flex flex-col">
      <div v-for="i in shimmerCount" :key="`shimmer-${i}`">
        <ConversationCardShimmer :id="`conversation-card-shimmer-${i}`" />
        <!-- Divider between shimmer cards -->
        <div v-if="i < shimmerCount" class="mx-4 border-b border-default" />
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="isEmpty" id="chat-panel-empty-state" class="flex h-full flex-col items-center justify-center px-6 py-12 text-center">
      <!-- Illustration -->
      <div class="mb-4 h-32 w-32">
        <OptimisedImage
          :src="DEFAULT_IMAGES.COMMUNITIES.CHATS_EMPTY_STATE"
          :alt="t('chats.images.altNoConversations')"
          :width="128"
          :height="128"
          :quality="100"
          format="webp"
          class="h-full w-full"
        />
      </div>

      <CPText size="2xl" weight="semibold" class="mb-2 text-body">
        {{ t('chats.emptyState.noConversations') }}
      </CPText>
      <CPText size="lg" class="mb-2 text-secondary">
        {{ t('chats.emptyState.noConversationsDescription') }}
      </CPText>
      <CPButton
        v-if="showStartChatButton"
        id="start-chat-button"
        type="secondary"
        size="sm"
        color="secondary"
        class="mt-2"
        @click="emit('startChat')"
      >
        {{ t('chats.actions.startChatting') }}
      </CPButton>
    </div>

    <!-- Conversation list -->
    <div v-else class="flex flex-col">
      <div v-for="(conversation, index) in conversations" :key="conversation._id">
        <ConversationListItem
          :conversation="conversation"
          :is-selected="conversation._id === selectedConversationId"
          :current-user-id="currentUserId"
          @click="handleConversationClick"
        />
        <!-- Divider (not full width) - don't show after last item -->
        <div v-if="index < conversations.length - 1" class="mx-4 border-b border-default" />
      </div>

      <!-- Load more trigger (intersection observer target) -->
      <div id="conversation-list-load-more-trigger" ref="loadMoreTriggerRef" class="h-4" />

      <!-- Loading more indicator with shimmer -->
      <template v-if="isFetchingNextPage">
        <div class="mx-4 border-b border-default" />
        <ConversationCardShimmer id="conversation-card-shimmer-next-1" />
        <div class="mx-4 border-b border-default" />
        <ConversationCardShimmer id="conversation-card-shimmer-next-2" />
      </template>
    </div>
  </div>
</template>

<style scoped>
  /* Custom thin scrollbar (consistent with AppSidebar) */
  .custom-scrollbar {
    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: var(--cp-font-300) transparent;
  }

  /* Webkit browsers (Chrome, Safari, Edge) */
  .custom-scrollbar::-webkit-scrollbar {
    width: 3px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: var(--cp-font-300);
    border-radius: 2px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: var(--cp-font-400);
  }
</style>
